/**
 * Modal for managing user assignments to a credential set.
 *
 * Shows currently assigned users and allows searching/adding
 * unassigned users or removing existing ones.
 */

import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/services/apiClient'
import {
  Button,
  Badge,
  Alert,
  AlertDescription,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Spinner,
} from '@/components/ui'
import { X, UserPlus, AlertTriangle } from 'lucide-react'

interface AssignedUser {
  user_id: string
  email: string
  display_name: string
  assigned_by_name: string | null
  assigned_at: string
}

interface AllUser {
  id: string
  email: string
  display_name: string
  role: string
  is_active: boolean
}

interface ManageSetUsersModalProps {
  setId: string
  setName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ManageSetUsersModal({ setId, setName, open, onOpenChange }: ManageSetUsersModalProps) {
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([])
  const [allUsers, setAllUsers] = useState<AllUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and selection
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isAssigning, setIsAssigning] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)

  // All user assignments for showing "already in set X" warnings
  const [allAssignments, setAllAssignments] = useState<Record<string, string>>({}) // user_id -> set_name

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, assignedRes, setsRes] = await Promise.all([
        apiClient.fetch('/api/admin/users'),
        apiClient.fetch(`/api/admin/credential-sets/${setId}/users`),
        apiClient.fetch('/api/admin/credential-sets'),
      ])

      if (!usersRes.ok || !assignedRes.ok || !setsRes.ok) throw new Error('Failed to fetch data')

      const users: AllUser[] = await usersRes.json()
      const assigned: AssignedUser[] = await assignedRes.json()
      const sets: Array<{ id: string; name: string }> = await setsRes.json()

      setAllUsers(users)
      setAssignedUsers(assigned)

      // Build a map of all user assignments across all sets
      const assignmentMap: Record<string, string> = {}
      for (const s of sets) {
        if (s.id === setId) continue // Skip current set
        const setUsersRes = await apiClient.fetch(`/api/admin/credential-sets/${s.id}/users`)
        if (setUsersRes.ok) {
          const setUsers: AssignedUser[] = await setUsersRes.json()
          for (const u of setUsers) {
            assignmentMap[u.user_id] = s.name
          }
        }
      }
      setAllAssignments(assignmentMap)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }, [setId])

  useEffect(() => {
    if (open) {
      setIsLoading(true)
      setSelectedIds(new Set())
      setSearch('')
      fetchData()
    }
  }, [open, fetchData])

  const assignedIds = new Set(assignedUsers.map((u) => u.user_id))

  const unassignedUsers = allUsers
    .filter((u) => !assignedIds.has(u.id) && u.is_active)
    .filter((u) => {
      if (!search.trim()) return true
      const q = search.toLowerCase()
      return u.display_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    })

  const toggleSelect = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const handleAssign = async () => {
    if (selectedIds.size === 0) return
    setIsAssigning(true)
    setError(null)
    try {
      const res = await apiClient.fetch(`/api/admin/credential-sets/${setId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ user_ids: Array.from(selectedIds) }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed' }))
        throw new Error(data.detail || 'Failed to assign users')
      }
      setSelectedIds(new Set())
      await fetchData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign users')
    } finally {
      setIsAssigning(false)
    }
  }

  const handleRemove = async (userId: string) => {
    setRemovingId(userId)
    try {
      const res = await apiClient.fetch(`/api/admin/credential-sets/${setId}/unassign`, {
        method: 'POST',
        body: JSON.stringify({ user_ids: [userId] }),
      })
      if (!res.ok) throw new Error('Failed to remove user')
      await fetchData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user')
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>Users assigned to: {setName}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              {/* Currently assigned */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Currently assigned ({assignedUsers.length})
                </h4>
                {assignedUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No users assigned yet.</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border">
                    {assignedUsers.map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between px-3 py-2">
                        <div>
                          <span className="text-sm font-medium text-foreground">
                            {u.display_name}
                          </span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {u.email}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(u.user_id)}
                          disabled={removingId === u.user_id}
                          className="text-destructive hover:text-destructive/90"
                          title="Remove from set"
                        >
                          {removingId === u.user_id ? (
                            <Spinner size="sm" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add users */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Add users
                </h4>
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mb-2"
                />
                <div className="max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border">
                  {unassignedUsers.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic px-3 py-3">
                      {search.trim() ? 'No matching users found.' : 'All users are already assigned.'}
                    </p>
                  ) : (
                    unassignedUsers.map((u) => {
                      const otherSet = allAssignments[u.id]
                      return (
                        <label
                          key={u.id}
                          className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-muted"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(u.id)}
                              onChange={() => toggleSelect(u.id)}
                              className="rounded-sm text-primary"
                            />
                            <div>
                              <span className="text-sm font-medium text-foreground">
                                {u.display_name}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {u.email}
                              </span>
                            </div>
                          </div>
                          {otherSet ? (
                            <Badge variant="warning" title={`Currently assigned to "${otherSet}". Will be replaced.`}>
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {otherSet}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">No set</span>
                          )}
                        </label>
                      )
                    })
                  )}
                </div>

                {Object.keys(allAssignments).length > 0 && selectedIds.size > 0 && (
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-warning" />
                    Users with a warning badge are assigned to another set. Assigning here will replace their current set.
                  </p>
                )}

                {selectedIds.size > 0 && (
                  <div className="mt-3">
                    <Button onClick={handleAssign} isLoading={isAssigning}>
                      <UserPlus className="w-4 h-4 mr-1.5" />
                      Assign {selectedIds.size} User{selectedIds.size !== 1 ? 's' : ''}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
